import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ThreeBackground = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ── Scene Setup ──────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 28);

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const CYAN   = 0x38bdf8;
    const CYAN2  = 0x22d3ee;

    // ── 1. TORUS KNOT — main centerpiece ────────────────────
    const tkGeo = new THREE.TorusKnotGeometry(7, 2.2, 160, 14);
    const tkPositions = tkGeo.attributes.position;
    const tkCount = tkPositions.count;

    const tkBuf = new THREE.BufferGeometry();
    const tkPos = new Float32Array(tkCount * 3);

    for (let i = 0; i < tkCount; i++) {
      tkPos[i * 3]     = tkPositions.getX(i);
      tkPos[i * 3 + 1] = tkPositions.getY(i);
      tkPos[i * 3 + 2] = tkPositions.getZ(i);
    }
    tkBuf.setAttribute('position', new THREE.BufferAttribute(tkPos, 3));

    const tkMat = new THREE.PointsMaterial({
      size: 0.09,
      color: CYAN,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const torusKnot = new THREE.Points(tkBuf, tkMat);
    // LEFT side — flanks the centered text
    torusKnot.position.x = -11;
    scene.add(torusKnot);

    // ── 1b. TORUS KNOT RIGHT — different p,q winding for visual variety ──
    const sphGeo = new THREE.TorusKnotGeometry(6.5, 1.8, 160, 14, 3, 4);
    const sphPositions = sphGeo.attributes.position;
    const sphCount = sphPositions.count;
    const sphBuf = new THREE.BufferGeometry();
    const sphPos = new Float32Array(sphCount * 3);
    for (let i = 0; i < sphCount; i++) {
      sphPos[i * 3]     = sphPositions.getX(i);
      sphPos[i * 3 + 1] = sphPositions.getY(i);
      sphPos[i * 3 + 2] = sphPositions.getZ(i);
    }
    sphBuf.setAttribute('position', new THREE.BufferAttribute(sphPos, 3));
    const sphMat = new THREE.PointsMaterial({
      size: 0.09,
      color: CYAN2,
      transparent: true,
      opacity: 0.52,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const sphereCloud = new THREE.Points(sphBuf, sphMat);
    // RIGHT side — counterpart to torus knot
    sphereCloud.position.x = 11;
    scene.add(sphereCloud);

    // ── 2. FLOATING WIREFRAME POLYHEDRA ──────────────────────
    const wireMat = (opacity = 0.18) =>
      new THREE.MeshBasicMaterial({
        color: CYAN,
        wireframe: true,
        transparent: true,
        opacity,
      });

    const floaters = [
      // Cluster on the right/center-right where torus knot lives
      { geo: new THREE.IcosahedronGeometry(1.8, 0), pos: [18,   7, -8],  speed: [0.008, 0.012, 0.006] },
      { geo: new THREE.OctahedronGeometry(1.4, 0),  pos: [20,  -6, -10], speed: [0.012, 0.007, 0.009] },
      { geo: new THREE.TetrahedronGeometry(1.2, 0), pos: [4,   -11, -6], speed: [0.007, 0.014, 0.010] },
      { geo: new THREE.IcosahedronGeometry(0.9, 0), pos: [14,   10, -5], speed: [0.015, 0.009, 0.007] },
      // A couple on far left (background, subtle)
      { geo: new THREE.OctahedronGeometry(0.7, 0),  pos: [-18, -6, -12], speed: [0.006, 0.008, 0.010] },
      { geo: new THREE.TetrahedronGeometry(0.6, 0), pos: [-15,  8, -14], speed: [0.009, 0.006, 0.007] },
    ].map(({ geo, pos, speed }) => {
      const mesh = new THREE.Mesh(geo, wireMat());
      mesh.position.set(...pos);
      mesh.userData.speed = speed;
      scene.add(mesh);
      return mesh;
    });

    // ── 3. NEURAL NETWORK — scattered nodes + dynamic lines ──
    const nodeCount = 72;
    const nodeBuf = new THREE.BufferGeometry();
    const nodePos = new Float32Array(nodeCount * 3);
    const nodeVel = new Float32Array(nodeCount * 3);

    for (let i = 0; i < nodeCount; i++) {
      nodePos[i * 3]     = (Math.random() - 0.5) * 50;
      nodePos[i * 3 + 1] = (Math.random() - 0.5) * 35;
      nodePos[i * 3 + 2] = (Math.random() - 0.5) * 18 - 5;
      nodeVel[i * 3]     = (Math.random() - 0.5) * 0.018;
      nodeVel[i * 3 + 1] = (Math.random() - 0.5) * 0.018;
      nodeVel[i * 3 + 2] = (Math.random() - 0.5) * 0.008;
    }
    nodeBuf.setAttribute('position', new THREE.BufferAttribute(nodePos, 3));
    nodeBuf.setAttribute('velocity', new THREE.BufferAttribute(nodeVel, 3));

    const nodeMat = new THREE.PointsMaterial({
      size: 0.12,
      color: CYAN2,
      transparent: true,
      opacity: 0.45,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const nodeSystem = new THREE.Points(nodeBuf, nodeMat);
    scene.add(nodeSystem);

    // Connection lines buffer
    const maxLines = 160;
    const lineBuf = new THREE.BufferGeometry();
    const linePos = new Float32Array(maxLines * 2 * 3);
    lineBuf.setAttribute('position', new THREE.BufferAttribute(linePos, 3));
    lineBuf.setDrawRange(0, 0);
    const lineMat = new THREE.LineBasicMaterial({
      color: CYAN2,
      transparent: true,
      opacity: 0.08,
      blending: THREE.AdditiveBlending,
    });
    const lineSegs = new THREE.LineSegments(lineBuf, lineMat);
    scene.add(lineSegs);

    // ── 4. DEEP BACKGROUND STARS ─────────────────────────────
    const starCount = 250;
    const starBuf = new THREE.BufferGeometry();
    const starPos = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      starPos[i * 3]     = (Math.random() - 0.5) * 120;
      starPos[i * 3 + 1] = (Math.random() - 0.5) * 80;
      starPos[i * 3 + 2] = (Math.random() - 0.5) * 40 - 20;
    }
    starBuf.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    const starMat = new THREE.PointsMaterial({
      size: 0.04,
      color: 0xffffff,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Points(starBuf, starMat));

    // ── 5. MOUSE PARALLAX ────────────────────────────────────
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMouseMove = (e) => {
      mouse.tx = (e.clientX / window.innerWidth  - 0.5) * 2;
      mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouseMove);

    // ── Animation ────────────────────────────────────────────
    let time = 0;
    let frameCount = 0;
    let paused = false;
    const onVisibility = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVisibility);

    const animate = () => {
      const raf = requestAnimationFrame(animate);
      if (paused) return;
      time += 0.004;
      frameCount++;

      // Smooth mouse follow
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;

      // Torus knot — complex multi-axis rotation
      torusKnot.rotation.x = time * 0.18 + mouse.y * 0.15;
      torusKnot.rotation.y = time * 0.28 + mouse.x * 0.15;
      torusKnot.rotation.z = time * 0.08;

      // Sphere cloud — counter-rotation for dynamic contrast
      sphereCloud.rotation.x = time * -0.12 + mouse.y * 0.12;
      sphereCloud.rotation.y = time *  0.22 + mouse.x * 0.12;
      sphereCloud.rotation.z = time * -0.06;

      // Parallax shift for the whole scene group
      scene.rotation.x = mouse.y * -0.04;
      scene.rotation.y = mouse.x *  0.06;

      // Wireframe floaters
      floaters.forEach((f) => {
        f.rotation.x += f.userData.speed[0];
        f.rotation.y += f.userData.speed[1];
        f.rotation.z += f.userData.speed[2];
        // Subtle float bob
        f.position.y += Math.sin(time + f.position.x) * 0.005;
      });

      // Neural network node movement
      const np = nodeSystem.geometry.attributes.position.array;
      const nv = nodeSystem.geometry.attributes.velocity.array;
      for (let i = 0; i < np.length; i += 3) {
        np[i]     += nv[i];
        np[i + 1] += nv[i + 1];
        np[i + 2] += nv[i + 2];
        if (Math.abs(np[i])     > 25) nv[i]     *= -1;
        if (Math.abs(np[i + 1]) > 18) nv[i + 1] *= -1;
        if (Math.abs(np[i + 2]) > 10) nv[i + 2] *= -1;
      }
      nodeSystem.geometry.attributes.position.needsUpdate = true;

      // Connection lines — update every 4 frames
      if (frameCount % 4 === 0) {
        let lIdx = 0;
        const THRESH_SQ = 6 * 6;
        const lp = lineSegs.geometry.attributes.position.array;
        for (let i = 0; i < nodeCount && lIdx < maxLines * 6; i++) {
          for (let j = i + 1; j < nodeCount && lIdx < maxLines * 6; j++) {
            const dx = np[i * 3] - np[j * 3];
            const dy = np[i * 3 + 1] - np[j * 3 + 1];
            const dz = np[i * 3 + 2] - np[j * 3 + 2];
            if (dx * dx + dy * dy + dz * dz < THRESH_SQ) {
              lp[lIdx++] = np[i * 3];     lp[lIdx++] = np[i * 3 + 1]; lp[lIdx++] = np[i * 3 + 2];
              lp[lIdx++] = np[j * 3];     lp[lIdx++] = np[j * 3 + 1]; lp[lIdx++] = np[j * 3 + 2];
            }
          }
        }
        lineSegs.geometry.setDrawRange(0, lIdx / 3);
        lineSegs.geometry.attributes.position.needsUpdate = true;
      }

      renderer.render(scene, camera);
      return raf;
    };

    const rafId = animate();

    // ── Resize ───────────────────────────────────────────────
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id="three-background"
      className="absolute top-0 left-0 w-full h-full z-0"
    />
  );
};

export default ThreeBackground;
